import {useSearchContext} from 'modules/reports/hooks/search'
import React, {useMemo} from 'react'
import ReportsTableContext, {ReportsTableContextProps} from './ReportsTable.context'
import useStyles from './ReportsTable.styles'
import ReportsTableColumn from './Column'
import {
  AutoSizer,
  Table,
  Column,
  InfiniteLoader,
  TableHeaderProps,
  TableHeaderRowProps,
  TableRowProps,
  TableCellProps,
} from 'react-virtualized'
import ReportsTableHeader from './Header'
import ReportsTableHeaderRow from './Header/Row'
import ReportsTableRow from './Row'
import ReportsTableCell from './Cell'
import useChildrenProps from 'modules/reports/hooks/childrenProps'
import {useTranslation} from 'react-i18next'
import usePrefix from 'modules/reports/hooks/prefix'
import {useBagContext} from 'modules/reports/contexts/bag'

type Props = {
  prefix: string
  fallback: () => React.ReactElement
  children: React.ReactNode
  href?: string
  onClick?: (item: any) => void
  scroll?: boolean
  compactRowHeight?: number
  comfortableRowHeight?: number
}

const DEFAULT_COMPACT_ROW_HEIGHT = 32
const DEFAULT_COMFORTABLE_ROW_HEIGHT = 48

function ReportsTable(props: Props) {
  const {
    prefix,
    fallback,
    children,
    onClick,
    scroll,
    compactRowHeight,
    comfortableRowHeight,
  } = props

  const classes = useStyles({scroll})
  const searchContext = useSearchContext()
  const bagContext = useBagContext()

  const {t} = useTranslation()
  const pt = usePrefix(prefix, '.')
  const [columns, otherComponents] = useChildrenProps(children, ReportsTableColumn)

  const size = searchContext.display.density === 'compact' ? 'small' : 'medium'

  const rowHeight = useMemo(() => {
    if (size === 'small') return compactRowHeight || DEFAULT_COMPACT_ROW_HEIGHT
    return comfortableRowHeight || DEFAULT_COMFORTABLE_ROW_HEIGHT
  }, [size, compactRowHeight, comfortableRowHeight])

  function rowGetter({index}) {
    return searchContext.data[index] || {}
  }

  function loadMoreRows({startIndex, stopIndex}) {
    return searchContext.fetchOffset(startIndex, stopIndex)
  }

  function isRowLoaded({index}) {
    return Boolean(searchContext.data[index])
  }

  function getRowClassName({index}) {
    const rowClasses = [classes.tableRow, classes.flexContainer]
    if (index !== -1 && onClick != null) rowClasses.push(classes.tableRowHover)

    return rowClasses.join(' ')
  }

  function handleClick(item: any) {
    if (onClick) onClick(item)
  }

  const contextValue: ReportsTableContextProps = {
    tablePrefix: prefix,
    tableColumns: columns,
    tableOnClick: handleClick,
    searchContext: searchContext,
    bag: bagContext,
  }

  function withContextValue<T>(
    fn: (props: T & ReportsTableContextProps) => React.ReactNode,
    additiontalProps?: any
  ) {
    return function(props: T) {
      return fn({...props, ...contextValue, ...(additiontalProps || {})})
    }
  }

  function onResize({height}) {
    searchContext.setRowCapacity(Math.ceil(height / rowHeight))
  }

  function handleSort({sortBy, sortDirection}) {
    const sortPrefix = sortDirection === 'ASC' ? '' : '-'
    searchContext.setSort(`${sortPrefix}${sortBy}`)
  }

  if (searchContext.response && !searchContext.total) return fallback()

  const headerClasses = [classes.tableCell, classes.flexContainer].join(' ')
  const cellClasses = [classes.tableCell, classes.flexContainer].join(' ')

  return (
    <ReportsTableContext.Provider value={contextValue}>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={searchContext.total}
      >
        {({onRowsRendered, registerChild}) => (
          <AutoSizer onResize={onResize}>
            {({height, width}) => (
              <Table
                ref={registerChild}
                height={height}
                width={width}
                rowCount={searchContext.total}
                rowGetter={rowGetter}
                rowHeight={rowHeight}
                headerHeight={rowHeight}
                headerRowRenderer={withContextValue<TableHeaderRowProps>(ReportsTableHeaderRow)}
                rowRenderer={withContextValue<TableRowProps>(ReportsTableRow)}
                onRowsRendered={onRowsRendered}
                rowClassName={getRowClassName}
                sort={handleSort}
                sortBy={searchContext.sortName}
                sortDirection={searchContext.sortDirection.toUpperCase() as any}
              >
                {columns.map(column => (
                  <Column
                    key={column.name}
                    label={column.noLabel ? null : t(`${pt()}.${column.name}`)}
                    dataKey={column.name}
                    width={column.width}
                    flexGrow={column.grow}
                    headerRenderer={withContextValue<TableHeaderProps>(ReportsTableHeader, {
                      className: headerClasses,
                    })}
                    cellRenderer={withContextValue<TableCellProps>(ReportsTableCell, {
                      className: cellClasses,
                    })}
                    columnData={column}
                    className={classes.flexContainer}
                  />
                ))}
              </Table>
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>

      {otherComponents}
    </ReportsTableContext.Provider>
  )
}

export default ReportsTable
